/**
 * 详情页数据管理 Hook
 *
 */

import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiResponse } from '../types/api';
import { isApiSuccess } from '../utils/api-helpers';
import {
  handleAbortableRequestError,
  handleApiFailure,
  runAbortableTask,
  startAbortableRequest,
} from './shared-async';
import { useLatestRef } from './useLatestRef';

/**
 * useDetailPage 配置
 */
export interface UseDetailPageOptions<T> {
  /** 详情数据请求 API */
  fetchApi: (id: string | number) => Promise<ApiResponse<T>>;
  /** 记录 ID */
  id: string | number | undefined;
  /** 是否在 ID 变化时自动请求，默认 true */
  autoFetch?: boolean;
  /** 成功状态码，默认 '200' */
  successCode?: string | number;
  /** 数据转换 */
  transformData?: (data: T) => T;
  /** 请求成功回调 */
  onSuccess?: (data: T) => void;
  /** 请求失败回调 */
  onError?: (error: Error) => void;
}

/**
 * useDetailPage 返回值
 */
export interface UseDetailPageReturn<T> {
  /** 详情数据 */
  data: T | null;
  /** 加载中状态 */
  loading: boolean;
  /** 错误信息 */
  error: Error | null;
  /** 手动获取指定 ID 的数据（适用于 autoFetch: false 场景） */
  fetch: (id: string | number) => Promise<void>;
  /** 手动刷新当前数据 */
  refresh: () => Promise<void>;
  /** 手动设置数据 */
  setData: Dispatch<SetStateAction<T | null>>;
}

/**
 * 详情页数据管理 Hook
 *
 * @example
 * ```tsx
 * const { data, loading, error, refresh } = useDetailPage({
 *   fetchApi: (id) => getUserDetail(id),
 *   id: params.id,
 *   onError: (err) => message.error(err.message),
 * });
 * ```
 */
export function useDetailPage<T>(
  options: UseDetailPageOptions<T>,
): UseDetailPageReturn<T> {
  const { fetchApi, id, autoFetch = true, successCode = '200' } = options;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // 记录当前 ID，用于 refresh 和手动 fetch 场景
  const currentIdRef = useLatestRef(id);

  // AbortController 引用，用于取消进行中的请求
  const abortControllerRef = useRef<AbortController | null>(null);

  // 用 useLatestRef 稳定回调引用，防止内联函数导致无限循环
  const fetchApiRef = useLatestRef(fetchApi);
  const transformDataRef = useLatestRef(options.transformData);
  const onSuccessRef = useLatestRef(options.onSuccess);
  const onErrorRef = useLatestRef(options.onError);

  const doFetch = useCallback(
    async (targetId: string | number) => {
      const controller = startAbortableRequest(abortControllerRef);

      setLoading(true);
      setError(null);

      await runAbortableTask({
        controller,
        setLoading,
        execute: async () => {
          const response = await fetchApiRef.current(targetId);

          // 请求已被取消，忽略结果
          if (controller.signal.aborted) return;

          const isSuccess = isApiSuccess(response, successCode);

          if (isSuccess && response.data) {
            const result = transformDataRef.current
              ? transformDataRef.current(response.data)
              : response.data;
            setData(result);
            onSuccessRef.current?.(result);
          } else {
            handleApiFailure({
              message:
                response.msg || response.message || response.errorMessage,
              fallbackMessage: 'Failed to fetch detail',
              setError,
              onError: onErrorRef.current,
            });
          }
        },
        onError: (error) =>
          handleAbortableRequestError({
            error,
            controller,
            setError,
            onError: onErrorRef.current,
          }),
      });
    },
    [successCode, fetchApiRef, onErrorRef, onSuccessRef, transformDataRef],
  );

  /** 手动获取指定 ID 的数据 */
  const fetchById = useCallback(
    async (targetId: string | number) => {
      currentIdRef.current = targetId;
      await doFetch(targetId);
    },
    [doFetch, currentIdRef],
  );

  /** 刷新当前数据 */
  const refresh = useCallback(async () => {
    const currentId = currentIdRef.current;
    if (currentId !== undefined && currentId !== null) {
      await doFetch(currentId);
    }
  }, [doFetch, currentIdRef]);

  useEffect(() => {
    if (autoFetch && id !== undefined && id !== null) {
      doFetch(id);
    }
    // 组件卸载或 id 变化时取消进行中的请求
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [autoFetch, id, doFetch]);

  return { data, loading, error, fetch: fetchById, refresh, setData };
}
